import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Check, Plus } from 'lucide-react'
import {
  Combobox,
  ComboboxContent,
  ComboboxTrigger,
} from 'tailwind-v4/components/popover-combobox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'tailwind-v4/components/command'
import { Button } from 'tailwind-v4/components/button'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'tailwind-v4/components/toggle-group'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import { defaultServicePagination } from '@/connect-types/backend/service'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { CreateOrganisationDialog } from '@/pages/organisations/create-organisation-dialog'
import { SwitcherChevron } from './switcher-chevron'

type SearchMode = 'organisations' | 'venues'

// The Stampede mark, inlined so it renders crisp at any size with no external
// fetch or padding-clip hack (the favicon PNG carries built-in padding). The
// viewBox is the tight-crop favicon; the brand gradient is the one rationed
// accent this control is allowed.
function StampedeMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 -15 143.7 143.7"
      fill="none"
      aria-hidden
      className={className}
    >
      <path
        d="M91.3 0H105.7V103L95.5 113.7H0V96.8L91.3 0Z"
        fill="url(#stampede-mark)"
      />
      <path
        d="M131.7 0H143.7V103L133.5 113.7H119.7V12.7L131.7 0Z"
        fill="url(#stampede-mark)"
      />
      <defs>
        <linearGradient
          id="stampede-mark"
          x1="40.6"
          y1="40.7"
          x2="146.6"
          y2="109.7"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EE2C86" />
          <stop offset="0.1" stopColor="#EF4B82" />
          <stop offset="0.2" stopColor="#F05B7C" />
          <stop offset="0.3" stopColor="#F16976" />
          <stop offset="0.4" stopColor="#F3756F" />
          <stop offset="0.5" stopColor="#F48067" />
          <stop offset="0.6" stopColor="#F5895E" />
          <stop offset="0.7" stopColor="#F69055" />
          <stop offset="0.8" stopColor="#F79849" />
          <stop offset="0.9" stopColor="#F89E40" />
          <stop offset="1" stopColor="#F9A238" />
        </linearGradient>
      </defs>
    </svg>
  )
}

function EntityInitial({ name }: { name: string }) {
  const initial = (name?.[0] ?? '?').toUpperCase()
  return (
    <span
      aria-hidden
      className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-[var(--accent)] text-[10px] font-semibold text-[var(--accent-foreground)]"
    >
      {initial}
    </span>
  )
}

export function OrgSwitcher() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [mode, setMode] = useState<SearchMode>('organisations')
  const [createOpen, setCreateOpen] = useState(false)
  const currentOrg = useSelectedOrganisation()
  const navigate = useNavigate()
  const { newUi } = useFeatureFlags()

  // Both queries run once the popover is open (not gated on `mode`) so
  // flipping the switch swaps between already-loaded lists instantly — no
  // reload flash, no popover re-render on every toggle.
  const { data: orgData, isLoading: orgsLoading } =
    morpheusApi.useGetOrganisationsSearchQuery(
      { limit: 20, search: search || undefined },
      { skip: !open }
    )
  const {
    data: venueData = defaultServicePagination,
    isLoading: venuesLoading,
  } = morpheusApi.useGetLocationsQuery(
    { limit: 20, cursor: '', search: search || undefined },
    { skip: !open }
  )

  const orgs = orgData?.data ?? []
  const venues = venueData.data
  const isLoading = mode === 'organisations' ? orgsLoading : venuesLoading

  return (
    <>
      <Combobox open={open} onOpenChange={setOpen}>
        <ComboboxTrigger className="w-full">
          <StampedeMark className="-ml-0.5 size-6 shrink-0" />
          <span className="min-w-0 truncate font-medium">
            {currentOrg?.name ?? 'Stampede'}
          </span>
          <SwitcherChevron className="ml-auto" />
        </ComboboxTrigger>
        <ComboboxContent
          className={`flex w-96 max-w-[calc(100vw-1rem)] flex-col p-0${
            newUi.enabled ? '' : ' old-ui-nav'
          }`}
        >
          <Command shouldFilter={false} className="min-h-0 flex-1">
            <CommandInput
              placeholder={
                mode === 'venues' ? 'Find venue…' : 'Find organisation…'
              }
              value={search}
              onValueChange={setSearch}
            />
            {/* Fixed height on desktop so switching org/venue lists doesn't
                resize + reposition the popover (mobile stays full-height). */}
            <CommandList className="md:h-72">
              {isLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading…
                </div>
              ) : null}

              {!isLoading && mode === 'organisations' ? (
                <>
                  <CommandEmpty>No organisation found.</CommandEmpty>
                  <CommandGroup>
                    {orgs.map((org) => {
                      const isCurrent = org.id === currentOrg?.id
                      return (
                        <CommandItem
                          key={org.id}
                          value={org.id}
                          onSelect={() => {
                            if (!isCurrent) {
                              navigate(`/${org.id}/venues`, {
                                viewTransition: true,
                              })
                            }
                            setOpen(false)
                          }}
                        >
                          <EntityInitial name={org.name ?? ''} />
                          <span className="truncate">{org.name}</span>
                          {isCurrent ? (
                            <Check className="ml-auto size-4" />
                          ) : null}
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </>
              ) : null}

              {!isLoading && mode === 'venues' ? (
                <>
                  <CommandEmpty>No venue found.</CommandEmpty>
                  <CommandGroup>
                    {venues.map((venue: any) => {
                      const label = venue.alias || venue.serial
                      return (
                        <CommandItem
                          key={venue.serial}
                          value={venue.serial}
                          onSelect={() => {
                            navigate(
                              `/${venue.organization_id}/venues/${venue.serial}`,
                              { viewTransition: true }
                            )
                            setOpen(false)
                          }}
                        >
                          <EntityInitial name={label ?? ''} />
                          <span className="truncate">{label}</span>
                          <span className="ml-auto shrink-0 font-mono text-xs text-muted-foreground">
                            {venue.serial}
                          </span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </>
              ) : null}
            </CommandList>
          </Command>

          {/* Sticky footer — always visible regardless of scroll position or
              search results: create a new organisation, and switch the
              searcher between organisations and venues. */}
          <div className="flex flex-shrink-0 flex-col gap-2 border-t border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start font-medium"
              leftIcon={<Plus />}
              onClick={() => {
                setOpen(false)
                setCreateOpen(true)
              }}
            >
              Create organisation
            </Button>
            <ToggleGroup
              value={[mode]}
              onValueChange={(next: string[]) => {
                const value = next[0]
                if (value) {
                  setMode(value as SearchMode)
                }
              }}
              variant="outline"
              className="w-full"
            >
              <ToggleGroupItem value="organisations" className="flex-1">
                Organisations
              </ToggleGroupItem>
              <ToggleGroupItem value="venues" className="flex-1">
                Venues
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </ComboboxContent>
      </Combobox>
      <CreateOrganisationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </>
  )
}
